import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'

interface Props {
  offer: App.BedbankOffer;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  checkOut?: string;
  flightOrigin?: App.Airport;
  canRedeemLuxPlusBenefits: boolean;
}

function BedbankHotelFlightsToggleButton(props: Props) {
  const {
    offer,
    rooms,
    checkIn,
    checkOut,
    flightOrigin,
    canRedeemLuxPlusBenefits,
  } = props

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkIn,
    endDate: checkOut,
    originAirportCode: flightOrigin?.code,
  })

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const showAll = packageViews.filter(p => p.available).length === 0

  const availablePackageViews = useMemo(() => packageViews.filter(
    packageView => (packageView.show && packageView.available) || (packageView.show && showAll),
  ), [packageViews, showAll])

  const { hotelPrice, hotelMemberPrice } = useMemo(() => {
    const getCheapestRatePrice = (packageView: App.BedbankOfferPackageView) => {
      if (packageView.rates.length > 0) {
        const cheapestRate = packageView.rates.reduce((prev, curr) => {
          const prevTotal = prev.totals.total
          const currTotal = curr.totals.total
          return prevTotal <= currTotal ? prev : curr
        })

        return {
          price: cheapestRate.totals.total,
          memberPrice: cheapestRate.totals.memberTotal ?? 0,
        }
      }

      return {
        price: 0,
        memberPrice: 0,
      }
    }

    // Get prices for all available package views
    const prices = availablePackageViews
      .map(getCheapestRatePrice)
      .filter(price => price.price > 0)

    if (prices.length === 0) {
      return {
        hotelPrice: 0,
        hotelMemberPrice: 0,
      }
    }

    // Find the cheapest price among all package views
    const cheapestPrice = prices.reduce((prev, curr) => {
      return prev.price <= curr.price ? prev : curr
    })

    return {
      hotelPrice: cheapestPrice.price,
      hotelMemberPrice: cheapestPrice.memberPrice,
    }
  }, [availablePackageViews])

  const showMemberPrice = canRedeemLuxPlusBenefits && hotelMemberPrice > 0
  const hotelTotalPrice = useMemo(() => showMemberPrice ? hotelMemberPrice : hotelPrice, [showMemberPrice, hotelMemberPrice, hotelPrice])
  const hotelFlightTotalPrice = hotelTotalPrice > 0 && flightPrice && flightPrice > 0 ? hotelTotalPrice + flightPrice : 0

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={offer.flights.flightsEnabled}
    isMemberPrice={showMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelFlightTotalPrice}
    pricesLoading={fetchingRates}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.bedbankOffer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  flightOrigin: state.airport,
  canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
}))(BedbankHotelFlightsToggleButton)
